<script lang="ts" setup>
import {
  Community,
  CommunityStatus,
  nameRules,
} from '@/models/community.model';
import { Municipality } from '@/models/municipality.model';
import { User } from '@/models/user.model';
import { useEvents, Event } from '@/services/events.composable';
import { FilterData } from '@/services/users.service';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import {
  MunicipalitiesActions,
  MunicipalityStoreState,
} from '@/stores/municipalities.store';
import { UsersActions, UsersState } from '@/stores/users.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref } from 'vue';
import { useStore } from 'vuex';

const store = useStore<
  ShiftCMSState & { us: UsersState; ms: MunicipalityStoreState }
>();

const events = useEvents();

const filterData = ref<FilterData>({
  search: '',
  level: null,
  page: 1,
  count: 25,
  sort: 'id',
  direction: 'asc',
});

function updateUsers(value: string) {
  filterData.value.search = value;
  store.dispatch(UsersActions.GetUserList, filterData.value);
}

const municipalities = computed<Municipality[]>(
  () => store.state.ms.municipalities,
);

const users = computed<User[]>(() => store.state.us.users);

const props = defineProps({
  data: {
    type: Object as PropType<Community>,
    required: true,
  },
});

const valid = ref<boolean>(false);

const community = ref<Community>({
  ...props.data,
});

const action = computed<string>(() =>
  community.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveCommunity = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (community.value.id === NEW_ID) {
      success = await store.dispatch(
        MunicipalitiesActions.AddCommunity,
        community.value,
      );
    } else {
      success = await store.dispatch(
        MunicipalitiesActions.UpdateCommunity,
        community.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved Community',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(UsersActions.GetCommunitiesList);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save Community',
      type: 'error',
    });
  }
};

onMounted(() => {
  if (props.data) {
    Object.assign(community.value, {
      ...props.data,
    });
  }
  if (props.data.id === NEW_ID) {
    store.dispatch(MunicipalitiesActions.GetMunicipalities);
  }
  if (!props.data.ambassador) {
    // store.dispatch(UsersActions.GetUsers);
  }
});

const deleteCommunity = async () => {
  if (
    community.value &&
    community.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "Community"?')
  ) {
    store.dispatch(MunicipalitiesActions.DeleteCommunity, community.value);

    store.dispatch(UsersActions.GetCommunitiesList);
    store.commit(Mutations.HideDrawer);
  }
};

const communityStatusOptions = [
  {
    title: 'Pending',
    value: CommunityStatus.Pending,
  },
  {
    title: 'Created',
    value: CommunityStatus.Created,
  },
];

const munitipalityName = ref<string>(community.value.municipality?.name || '');
const ambassadorName = ref<string>(
  `${community.value.ambassador?.firstName} ${community.value.ambassador?.lastName}`,
);

const municipalityOptions = computed(() =>
  municipalities.value.map((v) => ({
    title: v.name,
    value: v,
  })),
);
const userOptions = computed(() =>
  users.value
    .filter((u) => !!u.firstName && !!u.lastName)
    .map((v) => ({
      title: `${v.firstName} ${v.lastName} (id: ${v.id})`,
      value: v,
    })),
);
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveCommunity">
    <v-container>
      <h2>{{ $t(action) }} a community</h2>
      <v-row>
        <v-col class="mx-auto" v-if="community.id !== NEW_ID">
          <v-text-field
            v-model="community.name"
            :rules="nameRules"
            label="Name"
            required
          ></v-text-field>
        </v-col>
        <v-col class="mx-auto" v-else>
          <v-text-field
            v-model="community.name"
            label="Name (Automatically Generated)"
            disabled
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mx-auto" v-if="community.id !== NEW_ID">
          <v-text-field
            v-model="munitipalityName"
            label="Municipality"
            disabled
          ></v-text-field>
        </v-col>
        <v-col class="mx-auto" v-else>
          <v-select
            v-model="community.municipality"
            label="Municipality"
            :items="municipalityOptions"
          ></v-select>
        </v-col>

        <v-col
          class="mx-auto"
          v-if="community.id !== NEW_ID && data.ambassador"
        >
          <v-text-field
            v-model="ambassadorName"
            label="Ambassador"
            disabled
          ></v-text-field>
        </v-col>
        <v-col class="mx-auto" v-else>
          <v-autocomplete
            v-model="community.ambassador"
            :items="userOptions"
            @update:search="updateUsers"
            label="Ambassador"
          ></v-autocomplete>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mx-auto">
          <v-select
            v-model="community.status"
            :items="communityStatusOptions"
            label="Status"
            required
          ></v-select>
        </v-col>

        <v-col class="mx-auto">
          <v-text-field
            v-model="community.groupUrl"
            label="Signal Group URL"
            hint="Set the community status to 'created' when the signal group is ready."
            required
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mx-auto">
          <v-text-field
            v-model.number="community.userCount"
            :items="communityStatusOptions"
            label="Member count"
            hint="Shows how many members this community has"
            require
          ></v-text-field>
        </v-col>
        <v-col></v-col>
      </v-row>

      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn
            v-if="community.id !== NEW_ID"
            class="mt-2"
            type="button"
            color="warning"
            @click="deleteCommunity"
            block
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
pre {
  font-family: sans-serif;
  font-size: 12px;
  background-color: rgb(255, 230, 171);
  display: block;
  height: 100%;
  padding: 8px;
}
</style>
